import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { withStyles } from '@material-ui/core';
import styles from './styles';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import SideNavLayout from '../../../layouts/SideNavLayout';
import { TourService } from '../../../services/backend/tourService';
import { AuthService } from '../../../services/auth/authService';
import { displayModes } from '../../../services/enums/displayModes';
import FileSaver from 'file-saver';
import useDebounce from '../../../hooks/useDebounce';
import { SearchStopService } from '../../../services/searchServices/searchStopService';
import { stopMessageRelevantForFilter } from '../../../services/util/signalRMessageHelper';
import { useStopHub } from '../../../hooks/useStopHub';
import { DateService } from '../../../services/util/DateService';
import { userPermissions } from '../../../services/auth/userPermissions';
import { GetApp as DownloadIcon } from '@material-ui/icons';
import useLoadStopByIdCache from '../../../hooks/useLoadStopByIdCache';
import { DatePickerDefaultValueContext } from '../../../context';
import useMicroHubData from '../../../hooks/useMicroHubData';
import {
  GeneralInfoIcon,
  LoadingButton,
  LoadingIndicator,
  LoadingWrapper,
  Portlet,
  RefreshButton,
  StopDetails,
  StopFilter,
  StopTable,
  TourAndStopMap,
} from 'components';
import { StopStatus } from 'services/enums/StopStatus';
import {featureKeys, isFeatureEnabled} from "../../../featureAccess";

function OrderOverview(props) {
  const { classes, t } = props;
  const { fromDateDefault, toDateDefault, updateDefaultDate } = useContext(
    DatePickerDefaultValueContext
  );
  const [stops, setStops] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [tours, setTours] = useState([]);
  const [notPlanedStops, setNotPlanedStops] = useState([]);
  const {
    selectedStop,
    loadingSelectedStop,
    resetLoadStopByIdCache,
    getStopById,
    resetSelectedStop,
    updateCache,
  } = useLoadStopByIdCache();
  const [searchInput, setSearchInput] = useState('');
  const [backendLoadingStops, setBackendLoadingStops] = useState(false);
  const [backendLoadingTours, setBackendLoadingTours] = useState(false);

  const [mapTabState, setMapTabState] = useState(undefined);
  const [filter, setFilter] = useState({
    carrierName: '',
    shipperName: AuthService.getUserOrganization(),
    microHubName: '',
    stopStatus: '',
    fromDate: fromDateDefault,
    toDate: toDateDefault,
    boxId: '',
  });
  useEffect(
    () => updateDefaultDate(filter.fromDate),
    [filter.fromDate, updateDefaultDate]
  );

  const [searchResult, setSearchResult] = useState([]);
  const [backendLoadingExcel, setBackendLoadingExcel] = useState(false);
  const [carriersWithHubs, setCarriersWithHubs] = useState({});
  const { microHubData } = useMicroHubData();

  useStopHub(
    useCallback(
      (event) => {
        if (stopMessageRelevantForFilter(event, filter)) {
          setRefresh(true);
        }
      },
      [filter]
    )
  );
  
  useEffect(() => {
    if(stops && stops.length > 0) {
      const unplannedStops = stops.filter(x => x.stopStatus === StopStatus.NotPlaned);
      const stopsToCheckIfOnTour = stops.filter(x =>  x.stopStatus !== StopStatus.NotPlaned);
      const stopsOnTours = tours.flatMap(tour => tour.stops);
      
      if(stopsToCheckIfOnTour.length === stopsOnTours.length) {
        setNotPlanedStops(unplannedStops);
        return;
      }

      stopsToCheckIfOnTour.forEach(stopToCheck => {
        const indexOfStop = stopsOnTours.findIndex(x => x.tourStopId === stopToCheck.tourStopId)
        if(indexOfStop === -1) {
          unplannedStops.push(stopToCheck);
        }
      });
      setNotPlanedStops(unplannedStops);
    }
  }, [tours, stops])

  const loadTours = useCallback(() => {
    setBackendLoadingTours(true);
    TourService.getToursForShipperWithFilter(
      filter.carrierName,
      filter.shipperName,
      filter.microHubName,
      filter.fromDate,
      filter.toDate
    )
      .then((response) => response.json())
      .then(
        (response) => {
          setTours(response);
          setBackendLoadingTours(false);
          setRefresh(false);
        },
        () => {
          alert(t('errorLoadingTours'));
          setBackendLoadingTours(false);
          setRefresh(false);
        }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, t]);

  useEffect(() => {
    TourService.getStopsWithFilter(
      '',
      AuthService.getUserOrganization(),
      '',
      null,
      fromDateDefault,
      toDateDefault,
      ''
    )
      .then((response) => response.json())
      .then((response) => {
        // update carrier and microHub data for filter
        setCarriersWithHubs(getCarriersWithHubsFromStops(response));
      });
    // eslint-disable-next-line 
  }, []);

  const loadStops = useCallback(
    (selectedStop, noLoadingIndicator = false) => {
      if (!noLoadingIndicator) setBackendLoadingStops(true);
      setBackendLoadingStops(true);
      resetLoadStopByIdCache();
      TourService.getStopsWithFilter(
        filter.carrierName,
        filter.shipperName,
        filter.microHubName,
        filter.stopStatus === '' ? null : filter.stopStatus,
        filter.fromDate,
        filter.toDate,
        filter.boxId
      )
        .then((response) => response.json())
        .then(
          (response) => {
            setStops(response);
            const index = response.findIndex(
              (stop) =>
                selectedStop && stop.tourStopId === selectedStop.tourStopId
            );

            if (index >= 0) {
              getStopById(selectedStop.tourStopId);
            } else {
              resetSelectedStop();
            }

            if (!noLoadingIndicator) setBackendLoadingStops(false);
            setRefresh(false);
          },
          () => {
            alert(t('errorLoadingStops'));
            if (!noLoadingIndicator) setBackendLoadingStops(false);
            setRefresh(false);
          }
        );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, t]
  );

  function getCarriersWithHubsFromStops(stops) {
    return stops.reduce((result, stop) => {
      const carrier = stop.assignedCarrierName;
      const microHub = stop.assignedMicroHubName;
      if (carrier == null) {
        return result;
      }

      // Initialize the array for this carrier if it doesn't exist
      if (!result[carrier]) {
        result[carrier] = [];
      }

      // Add the microHub to the carrier's array if it's not already there
      if (microHub != null && !result[carrier].includes(microHub)) {
        result[carrier].push(microHub);
      }

      return result;
    }, {});
  }

  useEffect(() => {
    loadStops();
    loadTours();
  }, [loadStops, loadTours]);

  // use Debounce to prevent to many search operations, delay is in ms
  const debouncedSearchInput = useDebounce(searchInput, 250);

  // do search if debounceSearchInput changes
  useEffect(() => {
    const result = SearchStopService.searchStops(stops, debouncedSearchInput);
    setSearchResult(result);
  }, [debouncedSearchInput, stops]);

  const handleStopUpdate = (stop, neededUpdateBefore) => {
    if (neededUpdateBefore) {
      loadStops(setStops, true);
    } else {
      const index = stops.findIndex((x) => x.tourStopId === stop.tourStopId);
      const newStops = JSON.parse(JSON.stringify(stops));
      newStops[index] = stop;
      setStops(newStops);
      updateCache(stop);
      setRefresh(false);
    }
  };

  const showDetails = (stop) => {
    getStopById(stop.tourStopId);
  };

  const hideDetails = () => {
    resetSelectedStop();
  };

  const deleteStop = (stopId) => {
    TourService.deleteStoById(stopId).then(
      () => {
        const newStops = JSON.parse(JSON.stringify(stops));
        const index = newStops.findIndex((s) => s.tourStopId === stopId);
        if (index > -1) {
          newStops.splice(index, 1);
        }
        setStops(newStops);
        if (selectedStop?.tourStopId === stopId) resetSelectedStop();
      },
      () => {
        alert(t('errorDeletingStop'));
        loadStops(selectedStop);
      }
    );
  };

  const downloadStopsAsExcel = () => {
    if (stops.length !== 0) {
      setBackendLoadingExcel(true);
      TourService.downloadExcelShipper(
        filter.carrierName,
        filter.shipperName,
        filter.microHubName,
        filter.stopStatus === '' ? null : filter.stopStatus,
        filter.fromDate,
        filter.toDate
      ).then(
        (response) => {
          setBackendLoadingExcel(false);
          let filename = response.headers
            .get('Content-Disposition')
            .split('filename=')[1]
            .split(';')[0];

          response.blob().then((blob) => FileSaver.saveAs(blob, filename));
        },
        () => {
          alert(t('errorDownloadingExcel'));
          setBackendLoadingExcel(false);
        }
      );
    } else {
      alert(
        `${t('noStopDataAvailable')}: ${DateService.displayDate(
          filter.fromDate
        )}-${DateService.displayDate(filter.toDate)}`
      );
    }
  };

  const downloadEmptiesForStopsAsExcel = () => {
    if (stops.length !== 0) {
      if (stops.some((s) => s.editable)) alert(t('notAllStopsFinalEmpties'));
      setBackendLoadingExcel(true);
      TourService.downloadEmptiesForStopsAsExcelShipper(
        filter.carrierName,
        filter.shipperName,
        filter.microHubName,
        filter.stopStatus === '' ? null : filter.stopStatus,
        filter.fromDate,
        filter.toDate
      ).then(
        (response) => {
          setBackendLoadingExcel(false);
          if (response.status === 204) {
            alert(
              `${t('noStopsWithEmptiesFound')}: ${DateService.displayDate(
                filter.fromDate
              )}-${DateService.displayDate(filter.toDate)}`
            );
          } else {
            let filename = response.headers
              .get('Content-Disposition')
              .split('filename=')[1]
              .split(';')[0];

            response.blob().then((blob) => FileSaver.saveAs(blob, filename));
          }
        },
        () => {
          alert(t('errorDownloadingExcel'));
          setBackendLoadingExcel(false);
        }
      );
    } else {
      alert(
        `${t('noStopDataAvailable')}: ${DateService.displayDate(
          filter.fromDate
        )}-${DateService.displayDate(filter.toDate)}`
      );
    }
  };

  const downloadCustomerEmptiesBalanceAsExcel = () => {
    if (stops.length !== 0) {
      setBackendLoadingExcel(true);
      TourService.downloadCustomerEmptiesBalanceShipper(
          filter.carrierName,
          filter.shipperName,
          filter.microHubName,
          filter.stopStatus === '' ? null : filter.stopStatus,
          filter.fromDate,
          filter.toDate
      ).then(
          (response) => {
            setBackendLoadingExcel(false);
            if (response.status === 204) {
              alert(
                  `${t('noStopsWithEmptiesFound')}: ${DateService.displayDate(
                      filter.fromDate
                  )}-${DateService.displayDate(filter.toDate)}`
              );
            } else {
              let filename = response.headers
                  .get('Content-Disposition')
                  .split('filename=')[1]
                  .split(';')[0];

              response.blob().then((blob) => FileSaver.saveAs(blob, filename));
            }
          },
          () => {
            alert(t('errorDownloadingExcel'));
            setBackendLoadingExcel(false);
          }
      );
    } else {
      alert(
          `${t('noStopDataAvailable')}: ${DateService.displayDate(
              filter.fromDate
          )}-${DateService.displayDate(filter.toDate)}`
      );
    }
  };

  const updateStop = (stop) => {
    const needsRefresh = JSON.parse(JSON.stringify(refresh));
    TourService.updateStop(stop, stop.tourStopId)
      .then((response) => response.json())
      .then(
        (response) => {
          handleStopUpdate(response, needsRefresh);
        },
        () => {
          loadStops(selectedStop);
          alert(t('errorUpdatingStop'));
        }
      );
  };

  return (
    <SideNavLayout title={t('orderOverview')}>
      <div className={classes.root}>
        <div className={classes.helpWrapper}>
          <div className={classes.verticalContainer}>
            <LoadingButton
              color="primary"
              loading={backendLoadingExcel}
              onClick={downloadStopsAsExcel}
              variant="contained"
            >
              <DownloadIcon />
              &nbsp;{t('downloadOrderOverviewAsExcel')}
            </LoadingButton>
            <LoadingButton
              className={classes.emptiesDownloadButton}
              color="primary"
              loading={backendLoadingExcel}
              onClick={downloadEmptiesForStopsAsExcel}
              variant="contained"
            >
              <DownloadIcon />
              &nbsp;{t('emptiesDownload')}
            </LoadingButton>
            {isFeatureEnabled(featureKeys.downloadCustomerEmptiesBalanceExcel) && (
              <LoadingButton
                className={classes.emptiesDownloadButton}
                color="primary"
                loading={backendLoadingExcel}
                onClick={downloadCustomerEmptiesBalanceAsExcel}
                variant="contained"
              >
                <DownloadIcon />
                &nbsp;{t('customerEmptiesBalanceDownload')}
              </LoadingButton>
            )}
          </div>
          <div>
            <RefreshButton
              className={classes.buttonRight}
              refresh={refresh}
              refreshFunc={loadStops}
            />
            <GeneralInfoIcon />
          </div>
        </div>
        <br />
        <StopFilter
          carriersWithMicroHubs={carriersWithHubs}
          filter={filter}
          searchInput={searchInput}
          setFilter={setFilter}
          setSearchInput={setSearchInput}
          useSearch
        />
        <br />
        <LoadingWrapper loading={backendLoadingStops || backendLoadingTours}>
          <Fragment>
            <StopTable
              className={classes.content}
              deleteStop={
                AuthService.getUserPermission() === userPermissions.admin
                  ? deleteStop
                  : null
              }
              selectedStop={selectedStop}
              showDetails={showDetails}
              stops={searchResult}
              tableDisplayMode={displayModes.shipper}
            />
            {loadingSelectedStop && (
              <Portlet className={classes.content}>
                <LoadingIndicator />
              </Portlet>
            )}
            {selectedStop && (
              <StopDetails
                className={classes.content}
                closeDetailView={hideDetails}
                editDisplayMode={displayModes.shipper}
                microHubSelectOptions={microHubData}
                stop={selectedStop}
                updateStop={updateStop}
              />
            )}
            {stops && (
              <TourAndStopMap
                className={classes.tourMap}
                microHubs={microHubData}
                notPlanedStops={notPlanedStops ? notPlanedStops : []}
                persistedTabState={mapTabState}
                setPersistedTabState={setMapTabState}
                tours={tours}
              />
            )}
          </Fragment>
        </LoadingWrapper>
      </div>
    </SideNavLayout>
  );
}

OrderOverview.propTypes = {
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default compose(withStyles(styles), withTranslation())(OrderOverview);
